import type { TarotCard, DrawnCard, TarotSpread } from '../types/tarot';

/**
 * Shuffle an array using Fisher-Yates algorithm
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Draw cards from the deck for a reading
 */
export function drawCards(
  deck: TarotCard[], 
  spread: TarotSpread, 
  reversalChance: number = 0.5
): DrawnCard[] {
  const shuffledDeck = shuffleArray(deck);
  const drawnCards: DrawnCard[] = [];
  
  for (let i = 0; i < spread.cardCount; i++) {
    const card = shuffledDeck[i];
    const position = spread.positions[i];
    const isReversed = Math.random() < reversalChance;
    
    drawnCards.push({
      ...card,
      position: position.id,
      isReversed,
      positionMeaning: position.description
    });
  }
  
  return drawnCards;
}

/**
 * Get card meaning based on position and reversal
 */
export function getCardMeaning(card: DrawnCard): string[] {
  return card.isReversed ? card.meanings.reversed : card.meanings.upright;
}

/**
 * Generate basic interpretation for a reading
 */
export function generateBasicInterpretation(
  cards: DrawnCard[], 
  spread: TarotSpread,
  question: string
): string {
  let interpretation = `Reading for: "${question}"\n\n`;
  interpretation += `Spread: ${spread.name}\n`;
  interpretation += `${spread.description}\n\n`;
  
  cards.forEach((card, index) => {
    const position = spread.positions[index];
    const meanings = getCardMeaning(card);
    const reversalText = card.isReversed ? ' (Reversed)' : '';
    
    interpretation += `${position.name}: ${card.name}${reversalText}\n`;
    interpretation += `${position.description}\n`;
    interpretation += `Key meanings: ${meanings.join(', ')}\n\n`;
  });
  
  // Add general guidance
  interpretation += 'General Guidance:\n';
  interpretation += generateGeneralGuidance(cards);
  
  return interpretation;
}

/**
 * Generate general guidance based on drawn cards
 */
function generateGeneralGuidance(cards: DrawnCard[]): string {
  const majorArcanaCount = cards.filter(card => card.arcana === 'major').length;
  const reversedCount = cards.filter(card => card.isReversed).length;
  const suits = cards.filter(card => card.suit).map(card => card.suit);
  
  let guidance = '';
  
  // Major Arcana significance
  if (majorArcanaCount > cards.length / 2) {
    guidance += 'The high number of Major Arcana cards suggests this situation involves significant life lessons and spiritual growth. ';
  }
  
  // Reversal significance
  if (reversedCount > cards.length / 2) {
    guidance += 'Many reversed cards indicate internal conflicts or blocked energy that needs attention. ';
  }
  
  // Suit analysis for minor arcana
  const suitCounts = suits.reduce((acc, suit) => {
    if (suit) acc[suit] = (acc[suit] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const dominantSuit = Object.entries(suitCounts).sort(([,a], [,b]) => b - a)[0];
  
  if (dominantSuit) {
    const [suit, count] = dominantSuit;
    if (count >= 2) {
      switch (suit) {
        case 'wands':
          guidance += 'The prominence of Wands suggests this is about passion, creativity, and taking action. ';
          break;
        case 'cups':
          guidance += 'The prominence of Cups indicates emotional matters, relationships, and intuition are key. ';
          break;
        case 'swords':
          guidance += 'The prominence of Swords points to mental challenges, communication, and decision-making. ';
          break;
        case 'pentacles':
          guidance += 'The prominence of Pentacles suggests material concerns, career, and practical matters are important. ';
          break;
      }
    }
  }
  
  guidance += 'Trust your intuition as you reflect on these insights.';
  
  return guidance;
}

/**
 * Validate if a card combination makes sense
 */
export function validateReading(cards: DrawnCard[], spread: TarotSpread): boolean {
  return cards.length === spread.cardCount && 
         cards.every(card => card.position <= spread.cardCount);
}

/**
 * Get card image path with fallback
 */
export function getCardImagePath(card: TarotCard | DrawnCard): string {
  return card.imagePath || '/images/cards/backs/default.webp';
}

/**
 * Format card name for display
 */
export function formatCardName(card: TarotCard | DrawnCard): string {
  const reversalText = 'isReversed' in card && card.isReversed ? ' (Reversed)' : '';
  return `${card.name}${reversalText}`;
}

/**
 * Get spread difficulty level
 */
export function getSpreadDifficulty(spread: TarotSpread): 'beginner' | 'intermediate' | 'advanced' {
  if (spread.cardCount === 1) return 'beginner';
  if (spread.cardCount <= 3) return 'intermediate';
  return 'advanced';
}

/**
 * Estimate reading time in minutes
 */
export function estimateReadingTime(spread: TarotSpread): number {
  return Math.max(5, spread.cardCount * 2);
}